import { useRef } from 'react'
import { FileDown, Loader2, MousePointerClick, Sparkles, Upload } from 'lucide-react'

import { Button } from '@/components/ui/button'

const STEPS = [
  {
    icon: FileDown,
    title: 'Export from Google Docs',
    detail: 'In your document: File → Download → Markdown (.md). Save it anywhere.',
  },
  {
    icon: MousePointerClick,
    title: 'Upload it below',
    detail:
      'Use the button here, not the one in Post details. The file will be large — that is normal, the images are inside it.',
  },
  {
    icon: Sparkles,
    title: 'Check and publish',
    detail:
      'Images are pulled out and the text is tidied up automatically. Review the fields, fix anything that looks off, then Publish.',
  },
]

/**
 * The primary path for a non-technical author, and the only one that runs the
 * Google Docs pipeline (image extraction + AI clean-up). The button in Post
 * details takes an already-formatted file and imports it verbatim.
 *
 * Two buttons rather than one that guesses: guessing wrong either rewrites a
 * clean file through a model or dumps a 375KB base64 payload into the body.
 *
 * Deliberately not collapsible: for most people this is the whole workflow.
 */
export default function ImportInstructions({
  onFile,
  busy = false,
}: {
  onFile: (file: File) => void | Promise<void>
  busy?: boolean
}) {
  const input = useRef<HTMLInputElement>(null)

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-4">
      <h2 className="text-sm font-semibold text-violet-900">Writing in Google Docs?</h2>
      <ol className="mt-3 grid gap-3 sm:grid-cols-3">
        {STEPS.map((step, index) => (
          <li key={step.title} className="flex gap-2.5">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-200 text-xs font-semibold text-violet-900">
              {index + 1}
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-sm font-medium text-violet-900">
                <step.icon className="h-3.5 w-3.5 shrink-0" />
                {step.title}
              </span>
              <span className="mt-0.5 block text-xs leading-relaxed text-violet-800/80 text-left">
                {step.detail}
              </span>
            </span>
          </li>
        ))}
      </ol>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-violet-200 pt-3">
        <input
          ref={input}
          type="file"
          accept=".md,.markdown,text/markdown"
          className="hidden"
          onChange={event => {
            const file = event.target.files?.[0]
            if (file) void onFile(file)
            event.target.value = ''
          }}
        />
        <Button onClick={() => input.current?.click()} disabled={busy}>
          {busy ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Upload className="mr-1.5 h-4 w-4" />
          )}
          {busy ? 'Converting…' : 'Upload Google Docs .md'}
        </Button>
        <span className="text-left text-xs text-violet-800/70">
          Already have a file in the blog's format? Use{' '}
          <strong>Upload formatted .md</strong> in Post details — it is imported as-is, with no
          clean-up step.
        </span>
      </div>
    </div>
  )
}
